import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Users, BarChart3, TrendingUp, Settings,
  ExternalLink, CheckCircle2, Clock, AlertCircle,
  Instagram, Linkedin, Youtube, Plus, ArrowRight,
  Globe, Package, Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const PLANO_COLORS: Record<string, string> = {
  starter: "#7e7f7d",
  pro: "#c14727",
  enterprise: "#1e1d1d",
};

const PLANO_LABELS: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

const REDE_ICONS: Record<string, React.ReactNode> = {
  linkedin: <Linkedin className="w-3.5 h-3.5" />,
  instagram: <Instagram className="w-3.5 h-3.5" />,
  youtube: <Youtube className="w-3.5 h-3.5" />,
  tiktok: <span className="text-xs font-bold leading-none">TK</span>,
};

export default function RazMaster() {
  const { session: user } = useAuth();
  const { data: clients, isLoading } = trpc.clients.list.useQuery();

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Acesso restrito a administradores RAZ.</p>
        </div>
      </div>
    );
  }

  const ativos = clients?.filter(c => c.ativo) ?? [];
  const inativos = clients?.filter(c => !c.ativo) ?? [];

  const planoCount = {
    starter: ativos.filter(c => c.plano === "starter").length,
    pro: ativos.filter(c => c.plano === "pro").length,
    enterprise: ativos.filter(c => c.plano === "enterprise").length,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "#c14727" }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">RAZ Master</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Visão consolidada de todos os clientes gerenciados pela RAZ Consulting
          </p>
        </div>
        <Link href="/admin">
          <Button size="sm" className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Cliente
          </Button>
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{ativos.length}</p>
                <p className="text-xs text-muted-foreground">Clientes ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{planoCount.pro + planoCount.enterprise}</p>
                <p className="text-xs text-muted-foreground">Pro + Enterprise</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                <Globe className="w-4 h-4 text-orange-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{ativos.filter(c => c.subdomain).length}</p>
                <p className="text-xs text-muted-foreground">Subdomínios ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inativos.length}</p>
                <p className="text-xs text-muted-foreground">Inativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribuição por plano */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(["starter", "pro", "enterprise"] as const).map(plano => (
          <Card key={plano} className="border-l-4" style={{ borderLeftColor: PLANO_COLORS[plano] }}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{PLANO_LABELS[plano]}</p>
                  <p className="text-3xl font-bold mt-0.5">{planoCount[plano]}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {ativos.length > 0 ? Math.round((planoCount[plano] / ativos.length) * 100) : 0}%
                  </p>
                  <div className="w-16 h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{
                        width: `${ativos.length > 0 ? (planoCount[plano] / ativos.length) * 100 : 0}%`,
                        background: PLANO_COLORS[plano]
                      }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lista de clientes */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Clientes Ativos</h2>
          <Link href="/admin">
            <button className="text-xs text-primary flex items-center gap-1 hover:underline">
              Gerenciar todos <ArrowRight className="w-3 h-3" />
            </button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : ativos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">Nenhum cliente cadastrado ainda.</p>
              <Link href="/admin">
                <Button size="sm" className="mt-4 gap-2">
                  <Plus className="w-4 h-4" /> Cadastrar primeiro cliente
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {ativos.map(client => (
              <Card key={client.id} className="group hover:shadow-md transition-shadow overflow-hidden">
                {/* Barra de cor do cliente */}
                <div className="h-1.5 w-full" style={{ background: client.primaryColor ?? "#c14727" }} />
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                        style={{ background: client.primaryColor ?? "#c14727" }}>
                        {client.nome.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm leading-tight">{client.nome}</h3>
                        {client.subdomain && (
                          <p className="text-xs text-muted-foreground mt-0.5">{client.subdomain}.razconsulting.com.br</p>
                        )}
                      </div>
                    </div>
                    <Badge
                      className="text-xs font-medium border-0 flex-shrink-0"
                      style={{
                        background: `${PLANO_COLORS[client.plano ?? "starter"]}18`,
                        color: PLANO_COLORS[client.plano ?? "starter"]
                      }}>
                      {PLANO_LABELS[client.plano ?? "starter"]}
                    </Badge>
                  </div>

                  {client.descricao && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{client.descricao}</p>
                  )}

                  {/* Status */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">Ativo</span>
                    {client.subdomain && (
                      <>
                        <span className="text-muted-foreground/40 text-xs mx-1">·</span>
                        <Globe className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Subdomínio configurado</span>
                      </>
                    )}
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                    {client.subdomain && (
                      <a
                        href={`${window.location.protocol}//${client.subdomain}.${window.location.hostname.split(".").slice(-2).join(".")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Acessar
                      </a>
                    )}
                    <Link href="/admin">
                      <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground ml-auto">
                        <Settings className="w-3 h-3" />
                        Configurar
                      </button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Clientes inativos */}
      {inativos.length > 0 && (
        <div>
          <h2 className="text-base font-semibold mb-4 text-muted-foreground">Clientes Inativos ({inativos.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {inativos.map(client => (
              <Card key={client.id} className="opacity-60 border-dashed">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm bg-muted-foreground/30">
                      {client.nome.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">{client.nome}</h3>
                      <p className="text-xs text-muted-foreground">Inativo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
